/**
 * Modal shown after a successful second-pass submit when at least one
 * dimension disagreed — per `feature-spec.md` §3.4.7.
 *
 * Receives dimension *names* (already pre-resolved by the page from the
 * record's pinned taxonomy) so this component stays presentation-only.
 * Names arrive in server-returned order, which matches the record's
 * taxonomy ordering (B3) — we render them in the order received.
 *
 * Two CTAs in the footer: "Back to Queue" (outline) and
 * "Go to Review" (primary). Outside-click / X dismissal falls
 * through to `onOpenChange(false)`, which the parent treats as the
 * "Back to Queue" action.
 */
import React from 'react';

import { Badge } from '../../../../../ui/badge';
import { Button } from '../../../../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../../ui/dialog';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agreedCount: number;
  disputedDimensionNames: string[];
  onBackToQueue: () => void;
  onGoToReview: () => void;
}

const DisagreementModal: React.FC<Props> = ({
  open,
  onOpenChange,
  agreedCount,
  disputedDimensionNames,
  onBackToQueue,
  onGoToReview,
}) => {
  const disputedCount = disputedDimensionNames.length;
  const agreedWord = agreedCount === 1 ? 'dimension' : 'dimensions';
  const disputedWord = disputedCount === 1 ? 'dimension' : 'dimensions';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="disagreement-modal">
        <DialogHeader>
          <DialogTitle>Disagreement detected</DialogTitle>
          <DialogDescription>
            {agreedCount} {agreedWord} agreed · {disputedCount} {disputedWord} disputed.
            This record now needs adjudication.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2 py-2" data-testid="disputed-dimensions">
          {disputedDimensionNames.map((name, idx) => (
            <Badge
              key={`${name}-${idx}`}
              variant="destructive"
              data-testid={`disputed-dimension-${idx}`}
            >
              {name}
            </Badge>
          ))}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onBackToQueue}
            data-testid="disagreement-back-to-queue"
          >
            Back to Queue
          </Button>
          <Button
            type="button"
            onClick={onGoToReview}
            data-testid="disagreement-go-to-review"
          >
            Go to Review
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DisagreementModal;
